import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, putJson } from '../lib/api';

// --- Types ---

export type NotificationType =
  | 'task_assigned'
  | 'task_due'
  | 'case_assigned'
  | 'case_escalated'
  | 'comment_mention'
  | 'deal_update'
  | 'workflow_completed'
  | 'playbook_step'
  | 'system';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  entityType?: string;
  entityId?: string;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationFilters {
  isRead?: boolean;
  type?: NotificationType;
  limit?: number;
  offset?: number;
}

export interface NotificationsResponse {
  notifications: Notification[];
  total: number;
}

export interface UnreadCountResponse {
  count: number;
}

// --- Helpers ---

function buildQueryString(filters?: NotificationFilters): string {
  if (!filters) return '';
  const params = new URLSearchParams();
  if (filters.isRead !== undefined) params.set('isRead', String(filters.isRead));
  if (filters.type) params.set('type', filters.type);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.offset) params.set('offset', String(filters.offset));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// --- Hooks ---

export function useNotifications(filters?: NotificationFilters) {
  return useQuery({
    queryKey: ['notifications', filters],
    queryFn: () =>
      fetchJson<NotificationsResponse>(`/api/notifications${buildQueryString(filters)}`),
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => fetchJson<UnreadCountResponse>('/api/notifications/unread-count'),
    refetchInterval: 30_000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => putJson<void>(`/api/notifications/${id}/read`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => putJson<void>('/api/notifications/read-all', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
}
